import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { format } from "date-fns";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,  
} from "@mui/material";

import { RootState } from "../../../store/Index";
import { IRateCardProps } from "../types/rate-card";
import apiService from "../../../service/Api";
import { CalendarMonth, Event, HourglassBottom, Paid, Payment, ReceiptLong, Stream } from "@mui/icons-material";

interface ViewRateCardDialogProps {
  open: boolean;
  onClose: () => void;
  selectedRate: number | null;
};

export const ViewRateCardDialog: React.FC<ViewRateCardDialogProps> = ({
  open,
  onClose,
  selectedRate
}) => {
  const [rate, setRate] = useState<IRateCardProps | null>(null);
  const [rateLoading, setRateLoading] = useState<boolean>(false);
  const reduxTheme = useSelector((state: RootState) => state.theme);

  const retryRateCard = () => {
    fetchRateCard();
  };

  const fetchRateCard = useCallback(async () => {
    setRateLoading(true);
    try {
      const response = await apiService.get< IRateCardProps >(
        `/api/crews/role-ratings/${selectedRate}/`);

      setRate(response.data);
    } catch (error) {
      console.log(error, 'Failed to fetch rate card. Try Again');
    } finally {
      setRateLoading(false);
    }
  }, [selectedRate]);

  useEffect(() => {
    if (selectedRate) {
      fetchRateCard();
    };
  }, [fetchRateCard, selectedRate]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle>
        {rateLoading ? (
          <CircularProgress color={'primary'} size={16} />
        ) : (
          <Typography variant={'body2'} sx={{ fontWeight: 600 }}>
            {`Viewing The ${rate?.role.name} Rate Card`}
          </Typography>
        )}
      </DialogTitle>
      <DialogContent>
        <Box display={'flex'} alignItems={'center'} sx={{ mb: 1 }} gap={1}>
          <Typography variant={'body1'} sx={{ fontWeight: 600 }}>{rate?.role.name}</Typography>
          <Chip
            label={`ID: ${rate?.role.id}`}
            size={'small'}
            sx={{
              bgcolor: "transparent",
              border: '1px solid',
              borderColor: "#724E41",
              fontWeight: 600,
              textTransform: "uppercase",
              color: "#724E41",
              borderRadius: 1
            }}
          />
        </Box>

        <Box display={'flex'} alignItems={'center'} sx={{ mb: 1 }} gap={1}>
          <Typography variant={'body2'}>{'Role Description:'}</Typography>
          <Typography variant={'body2'}>{rate?.role.description}</Typography>
        </Box>
        
        {rateLoading ? (
          <CircularProgress color={'primary'} size={16} />
        ) : !rate ? (
          <Alert severity="warning" sx={{ alignItems: "center" }}>
            <Box display={"flex"} alignItems={"center"} gap={1}>
              {"Could not fetch rate card."}
              <Button
                onClick={() => retryRateCard()}
                sx={{
                  color: reduxTheme.primary_colors[400],
                  backgroundColor: reduxTheme.primary_colors[900],
                }}
              >
                Retry
              </Button>
            </Box>
          </Alert>
        ) : (
          <Box sx={{ mt: 2 }}>
            <Box display={'flex'} alignItems={'center'} sx={{ borderBottom: "1px dotted", py: .8 }} justifyContent={'space-between'}>
              <Box display={'flex'} alignItems={'center'} gap={.8}>
                <CalendarMonth sx={{ fontSize: 18, color: "#724E41" }} />
                <Typography variant={'body2'}>Effective Date</Typography>
              </Box>

              <Box display={'flex'} sx={{ textAlign: "right" }} flexDirection={'column'}>
                <Typography variant={'body2'} sx={{ fontWeight: 600 }}>{format(new Date(rate.effective_date), "dd MMM, yyyy")}</Typography>
              </Box>
            </Box>

            <Box display={'flex'} alignItems={'center'} sx={{ borderBottom: "1px dotted", py: .8 }} justifyContent={'space-between'}>
              <Box display={'flex'} alignItems={'center'} gap={.8}>
                <Paid sx={{ fontSize: 18, color: "#724E41" }} />
                <Typography variant={'body2'}>Currency</Typography>
              </Box>

              <Box display={'flex'} sx={{ textAlign: "right" }} flexDirection={'column'}>
                <Typography variant={'body2'} sx={{ fontWeight: 600 }}>{rate.currency}</Typography>
              </Box>
            </Box>

            <Box display={'flex'} alignItems={'center'} sx={{ borderBottom: "1px dotted", py: .8 }} justifyContent={'space-between'}>
              <Box display={'flex'} alignItems={'center'} gap={.8}>
                <HourglassBottom sx={{ fontSize: 18, color: "#724E41" }} />
                <Typography variant={'body2'}>Hourly Rate Amount</Typography>
              </Box>

              <Box display={'flex'} sx={{ textAlign: "right" }} flexDirection={'column'}>
                <Typography variant={'body2'} sx={{ fontWeight: 600 }}>{`$${rate.rate.hourly.amount}`}</Typography>
              </Box>
            </Box>

            <Box display={'flex'} alignItems={'center'} sx={{ borderBottom: "1px dotted", py: .8 }} justifyContent={'space-between'}>
              <Box display={'flex'} alignItems={'center'} gap={.8}>
                <Stream sx={{ fontSize: 18, color: "#724E41" }} />
                <Typography variant={'body2'}>Stream Rate Amount</Typography>
              </Box>

              <Box display={'flex'} sx={{ textAlign: "right" }} flexDirection={'column'}>
                <Typography variant={'body2'} sx={{ fontWeight: 600 }}>{`$${rate.rate.stream.amount}`}</Typography>
              </Box>
            </Box>

            <Box display={'flex'} alignItems={'center'} sx={{ borderBottom: "1px dotted", py: .8 }} justifyContent={'space-between'}>
              <Box display={'flex'} alignItems={'center'} gap={.8}>
                <Event sx={{ fontSize: 18, color: "#724E41" }} />
                <Typography variant={'body2'}>Daily Rate Amount</Typography>
              </Box>

              <Box display={'flex'} sx={{ textAlign: "right" }} flexDirection={'column'}>
                <Typography variant={'body2'} sx={{ fontWeight: 600 }}>{`$${rate.rate.daily.amount}`}</Typography>
              </Box>
            </Box>

            <Box display={'flex'} alignItems={'center'} sx={{ borderBottom: "1px dotted", py: .8 }} justifyContent={'space-between'}>
              <Box display={'flex'} alignItems={'center'} gap={.8}>
                <ReceiptLong sx={{ fontSize: 18, color: "#724E41" }} />
                <Typography variant={'body2'}>Salaried Rate Amount</Typography>
              </Box>

              <Box display={'flex'} sx={{ textAlign: "right" }} flexDirection={'column'}>
                <Typography variant={'body2'} sx={{ fontWeight: 600 }}>{`$${rate.rate.salaried.amount}`}</Typography>
              </Box>
            </Box>

            <Box display={'flex'} alignItems={'center'} sx={{ borderBottom: "1px dotted", py: .8 }} justifyContent={'space-between'}>
              <Box display={'flex'} alignItems={'center'} gap={.8}>
                <Payment sx={{ fontSize: 18, color: "#724E41" }} />
                <Typography variant={'body2'}>Fixed Rate Amount</Typography>
              </Box>

              <Box display={'flex'} sx={{ textAlign: "right" }} flexDirection={'column'}>
                <Typography variant={'body2'} sx={{ fontWeight: 600 }}>{`$${rate.rate.fixed.amount}`}</Typography>
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={onClose}
          sx={{ backgroundColor: reduxTheme.primary_colors[400] }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ViewRateCardDialog;
